import type { StrategyNode } from './BaseNode';
import { v4 as uuid } from 'uuid';
import { isStrategyNode } from './typeGuards';
import { reinstantiate } from '../utils/nodeFactory';

export class CompositeNode implements StrategyNode {
    id: string;
    type: 'Composite' = 'Composite';
    title?: string;
    isManualRename?: boolean;
    config: {
        combine_on?: string;
        operator?: string;
        left?: StrategyNode;
        right?: StrategyNode;
        window?: number;
    };

    constructor(
        id: string,
        title?: string,
        operator?: string,
        combine_on?: string,
        left?: StrategyNode,
        right?: StrategyNode,
        isManualRename?: boolean
    ) {
        this.id = id;
        this.title = title;
        this.isManualRename = isManualRename;
        this.config = { 
            operator, 
            combine_on, 
            left: left ? reinstantiate(left) : undefined, 
            right: right ? reinstantiate(right) : undefined 
        };
    }

    update(id: string, patch: Partial<StrategyNode>): StrategyNode {
        // If the patch is meant for this node itself
        if (this.id === id) {
            const newNode = new CompositeNode(
                this.id,
                patch.title !== undefined ? patch.title : this.title,
                (patch as any).config?.operator ?? this.config.operator,
                (patch as any).config?.combine_on ?? this.config.combine_on,
                (patch as any).config?.left ?? this.config.left,
                (patch as any).config?.right ?? this.config.right,
                patch.isManualRename !== undefined ? patch.isManualRename : this.isManualRename
            );
            newNode.config.window = (patch as any).config?.window ?? this.config.window;
            return newNode;
        }

        // Recursively update left/right
        const updatedLeft = this.config.left?.update(id, patch);
        const updatedRight = this.config.right?.update(id, patch);

        if (updatedLeft !== this.config.left || updatedRight !== this.config.right) {
            const result = new CompositeNode(
                this.id,
                this.title,
                this.config.operator,
                this.config.combine_on,
                updatedLeft,
                updatedRight,
                this.isManualRename
            );
            result.config.window = this.config.window;
            return result;
        }

        return this;
    }

    clone(): StrategyNode {
        const cloned = new CompositeNode(
            uuid(),
            this.title,
            this.config.operator,
            this.config.combine_on,
            isStrategyNode(this.config.left) ? this.config.left.clone() : undefined,
            isStrategyNode(this.config.right) ? this.config.right.clone() : undefined,
            this.isManualRename
        );
        cloned.config.window = this.config.window;
        return cloned;
    }

}